"use client";

import React from "react";
import _ from "lodash";

interface HeavyCalculatorProps {
  title?: string;
}

const HeavyCalculator: React.FC<HeavyCalculatorProps> = ({
  title = "Complex Calculator",
}) => {
  const [result, setResult] = React.useState<number>(0);
  const [history, setHistory] = React.useState<string[]>([]);

  // Simulate heavy computational work using lodash
  const performHeavyCalculation = (operation: string) => {
    const startTime = performance.now();

    let newResult: number;
    const currentResult = result;

    switch (operation) {
      case "fibonacci":
        // Calculate Fibonacci using lodash memoization
        const fibonacci = _.memoize((n: number): number => {
          if (n <= 1) return n;
          return fibonacci(n - 1) + fibonacci(n - 2);
        });
        newResult = fibonacci(Math.min(currentResult + 5, 30));
        break;

      case "prime":
        // Find nth prime number
        const isPrime = (num: number) => {
          if (num < 2) return false;
          return _.range(2, Math.sqrt(num) + 1).every((i) => num % i !== 0);
        };
        const primes = _.range(2, 1000).filter(isPrime);
        newResult = primes[Math.min(currentResult, primes.length - 1)] || 2;
        break;

      case "factorial":
        const factorial = _.memoize((n: number): number => {
          if (n <= 1) return 1;
          return n * factorial(n - 1);
        });
        newResult = factorial(Math.min(currentResult + 1, 10));
        break;

      case "matrix":
        // Matrix multiplication simulation
        const size = Math.min(currentResult + 2, 10);
        const matrix1 = _.range(size).map(() =>
          _.range(size).map(() => _.random(1, 10))
        );
        const matrix2 = _.range(size).map(() =>
          _.range(size).map(() => _.random(1, 10))
        );

        const multiplyMatrices = (a: number[][], b: number[][]) => {
          return a.map((row) =>
            b[0].map((_, j) =>
              row.reduce((sum, elm, k) => sum + elm * b[k][j], 0)
            )
          );
        };

        const resultMatrix = multiplyMatrices(matrix1, matrix2);
        newResult = _.sum(_.flatten(resultMatrix)) % 1000;
        break;

      default:
        newResult = _.random(1, 100);
    }

    const endTime = performance.now();
    const duration = _.round(endTime - startTime, 2);

    setResult(newResult);
    setHistory((prev) => [
      `${operation}: ${newResult} (${duration}ms)`,
      ...prev.slice(0, 9),
    ]);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>

      <div className="mb-6">
        <div className="text-center mb-4">
          <span className="text-3xl font-bold text-blue-600">{result}</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => performHeavyCalculation("fibonacci")}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Fibonacci
          </button>
          <button
            onClick={() => performHeavyCalculation("prime")}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors"
          >
            Prime Numbers
          </button>
          <button
            onClick={() => performHeavyCalculation("factorial")}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded transition-colors"
          >
            Factorial
          </button>
          <button
            onClick={() => performHeavyCalculation("matrix")}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors"
          >
            Matrix Math
          </button>
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-2">
            Calculation History:
          </h4>
          <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
            {history.map((entry, index) => (
              <div key={index} className="text-sm text-gray-600 mb-1">
                {entry}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-sm text-gray-600 mt-4">
        This component performs heavy calculations using Lodash utilities and
        memoization
      </p>
    </div>
  );
};

export default HeavyCalculator;
